/* eslint-disable react/forbid-prop-types */
import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

const CookiesAccept = lazy(() => import('./components/CookiesAccept'));
const ScrollToTop = lazy(() => import('./shared/ScrollToTop'));

const Homepage = lazy(() => import('./components/homepage/HomePage'));
const Register = lazy(() => import('./components/register/RegisterPage'));
const Login = lazy(() => import('./components/login/LoginPage'));
const Logout = lazy(() => import('./components/logout/LogoutPage'));
const Account = lazy(() => import('./components/account/AccountPage'));
const Profile = lazy(() => import('./components/indv_pages/ProfilePage'));
const ClassFinder = lazy(() => import('./components/finder/class/ClassFinder'));
const TrainingFinder = lazy(() => import('./components/finder/training/TrainingFinder'));
const TeacherFinder = lazy(() => import('./components/finder/teacher/Finder'));
const TrainerFinder = lazy(() => import('./components/finder/trainer/Finder'));
const Class = lazy(() => import('./components/indv_pages/ClassPage'));
const Location = lazy(() => import('./components/indv_pages/LocationPage'));
const PrivacyPage = lazy(() => import('./components/indv_pages/PrivacyPage'));
const TermsPage = lazy(() => import('./components/indv_pages/TermsPage'));
const CCPAPage = lazy(() => import('./components/indv_pages/CCPAPage'));
const ContactPage = lazy(() => import('./components/indv_pages/ContactPage'));
const AboutMembershipPage = lazy(() => import('./components/indv_pages/AboutMembershipPage'));
const SpecialtyTrainings = lazy(() => import('./components/trainings/specialtytrainings/SpecialtyTrainingsPage'));
const BodyLifeTrainings = lazy(() => import('./components/trainings/bodylifetrainings/BodyLifeTrainingsPage'));
const Shop = lazy(() => import('./components/shop/ShopPage'));
const Activate = lazy(() => import('./components/indv_pages/ActivatePage'));
const RecoverAccount = lazy(() => import('./components/account_recovery/RecoverAccountPage'));
const ResetPassword = lazy(() => import('./components/account_recovery/ResetPasswordPage'));
const NotFoundPage = lazy(() => import('./components/indv_pages/NotFoundPage'));
const Join = lazy(() => import('./components/indv_pages/JoinPage'));
const AboutEvents = lazy(() => import('./components/indv_pages/AboutEventsPage'));
const AboutClasses = lazy(() => import('./components/indv_pages/AboutClassesPage'));
const AboutTrainings = lazy(() => import('./components/indv_pages/AboutTrainingsPage'));
const DebbieRosasEvents = lazy(() => import('./components/events/DebbieRosasEventsPage'));
const PressRoom = lazy(() => import('./components/indv_pages/PressRoom'));
const AboutNiaPage = lazy(() => import('./components/indv_pages/AboutNiaPage'));
const ClassTypesPage = lazy(() => import('./components/classes/ClassTypesPage'));
const TeachPage = lazy(() => import('./components/indv_pages/TeachPage'));
const DEICommitmentPage = lazy(() => import('./components/indv_pages/DEICommitmentsPage'));
const SignupThankYou = lazy(() => import('./components/indv_pages/EmailSignupThankYou'));
const MasqueradePage = lazy(() => import('./components/masquerade/MasqueradePage'));
const Blog = lazy(() => import('./components/blog/Blog'));
const BlogPostDetail = lazy(() => import('./components/blog/BlogPostDetail'));
const AboutBeltsPage = lazy(() => import('./components/indv_pages/AboutBeltsPage'));
const FAQPage = lazy(() => import('./components/indv_pages/FAQPage'));
const NiaDayGuidePage = lazy(() => import('./components/leadgen_pages/NiaDayGuidePage'));
const NiaDayGuideThankYou = lazy(() => import('./components/leadgen_pages/NiaDayGuideThankYou'));
const HolisticFitnessPage = lazy(() => import('./components/leadgen_pages/HolisticFitnessPage'));
const HolisticFitnessThankYouPage = lazy(() => import('./components/leadgen_pages/HolisticFitnessThankYouPage'));
const BarefootDayPage = lazy(() => import('./components/leadgen_pages/BarefootDayPage'));
const BarefootDayThankYou = lazy(() => import('./components/leadgen_pages/BarefootDayThankYou'));
const SalePage = lazy(() => import('./components/indv_pages/SalePage'));
const TestimonialsPage = lazy(() => import('./components/indv_pages/TestimonialsPage'));
// const MaintenanceMode = lazy(() => import('./components/MaintenanceMode'));

const styles = () => ({
  root: {
    display: 'flex',
  },
  loading: {
    height: '100vh',
    width: '100vw',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
});

// Layout component wrapping all routes - provides Suspense, ScrollToTop, and CookiesAccept
function AppLayout({ classes }) {
  return (
    <Suspense fallback={<div className={classes.loading}><CircularProgress color="secondary" /></div>}>
      <ScrollToTop>
        <div className={classes.root}>
          <Outlet />
        </div>
      </ScrollToTop>
      <CookiesAccept />
    </Suspense>
  );
}

AppLayout.propTypes = {
  classes: PropTypes.object.isRequired,
};

const StyledAppLayout = withStyles(styles)(AppLayout);

// Data router - required for useBlocker (NavigationBlocker) to work
const router = createBrowserRouter(
  [
    {
      element: <StyledAppLayout />,
      children: [
        { path: '/', element: <Homepage /> },
        /* REDIRECTS */
        { path: '/geofind', element: <Navigate to="/finder/class" replace /> },
        { path: '/finder/class/inperson', element: <Navigate to="/finder/class?attendance_type=inperson" replace /> },
        { path: '/finder/class/online', element: <Navigate to="/finder/class?attendance_type=online" replace /> },
        { path: '/finder/training/inperson', element: <Navigate to="/finder/training?attendance_type=inperson" replace /> },
        { path: '/finder/training/online', element: <Navigate to="/finder/training?attendance_type=online" replace /> },
        /* USER */
        { path: '/register', element: <Register /> },
        { path: '/activate/:id/:token', element: <Activate /> },
        { path: '/account_recovery', element: <RecoverAccount /> },
        { path: '/password_reset/:id/:token', element: <ResetPassword /> },
        { path: '/login', element: <Login /> },
        { path: '/logout', element: <Logout /> },
        /* ACCOUNT */
        { path: '/account/*', element: <Account /> },
        /* FINDER */
        { path: '/finder/class/:query?', element: <ClassFinder /> },
        { path: '/finder/training/:query?', element: <TrainingFinder /> },
        { path: '/finder/teacher/', element: <TeacherFinder /> },
        { path: '/finder/trainer/', element: <TrainerFinder /> },
        /* BLOG */
        { path: '/blog', element: <Blog /> },
        { path: '/blog/:idOrSlug', element: <BlogPostDetail /> },
        /* INDV PAGES */
        { path: '/about-nia', element: <AboutNiaPage /> },
        { path: '/user/:userNameOrId', element: <Profile /> },
        { path: '/class/:id', element: <Class /> },
        { path: '/contact', element: <ContactPage /> },
        { path: '/terms', element: <TermsPage /> },
        { path: '/privacy', element: <PrivacyPage /> },
        { path: '/ccpa', element: <CCPAPage /> },
        { path: '/membership', element: <AboutMembershipPage /> },
        { path: '/location/:id', element: <Location /> },
        { path: '/specialtytrainings/:trainingType?', element: <SpecialtyTrainings /> },
        { path: '/trainings/:trainingType?', element: <BodyLifeTrainings /> },
        { path: '/join', element: <Join /> },
        { path: '/class-intro', element: <AboutClasses /> },
        { path: '/classes/:classType?', element: <ClassTypesPage /> },
        { path: '/training-intro', element: <AboutTrainings /> },
        { path: '/event-intro', element: <AboutEvents /> },
        { path: '/debbie-rosas-events', element: <DebbieRosasEvents /> },
        { path: '/press-room', element: <PressRoom /> },
        { path: '/teach', element: <TeachPage /> },
        { path: '/dei', element: <DEICommitmentPage /> },
        { path: '/masquerade/:username/:token', element: <MasqueradePage /> },
        { path: '/signup_thankyou', element: <SignupThankYou /> },
        { path: '/belt-tutorials', element: <AboutBeltsPage /> },
        { path: '/faq', element: <FAQPage /> },
        { path: '/sale', element: <SalePage /> },
        { path: '/testimonials', element: <TestimonialsPage /> },
        /* LEAD GEN */
        { path: '/nia-day-guide', element: <NiaDayGuidePage /> },
        { path: '/nia-day-thankyou', element: <NiaDayGuideThankYou /> },
        { path: '/holistic-fitness-report', element: <HolisticFitnessPage /> },
        { path: '/holistic-fitness-thankyou', element: <HolisticFitnessThankYouPage /> },
        { path: '/barefoot-day', element: <BarefootDayPage /> },
        { path: '/barefoot-day-thankyou', element: <BarefootDayThankYou /> },
        /* SHOP */
        { path: '/shop/*', element: <Shop /> },
        /* NOT FOUND (must be last) */
        { path: '/404', element: <NotFoundPage /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  },
);

function Routes() {
  return <RouterProvider router={router} />;
}

export default Routes;

// uncomment for maintenance mode
// const maintenanceRouter = createBrowserRouter([
//   { path: '*', element: <MaintenanceMode /> }
// ]);
// function Routes() {
//   return <RouterProvider router={maintenanceRouter} />;
// }
